/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

const chipStyles = ({selected, orderable, theme}) => {
    const {textStyles} = theme

    return {
        swatch: {
            display: 'flex',
            alignItems: 'center',
            borderStyle: orderable ? 'solid' : 'dashed',
            borderWidth: selected ? '2px' : '1px',
            borderColor: selected ? 'stokkeCore.black' : 'stokkeGray.mid',
            borderRadius: 5,
            paddingLeft: 2,
            paddingRight: 4,
            paddingTop: 1,
            paddingBottom: 1,
            height: 'auto',
            minHeight: 37,
            whiteSpace: {base: 'nowrap', md: 'normal'},
            ...textStyles['Regular Small'],
            _hover: {
                borderWidth: '2px',
                borderColor: 'stokkeCore.black'
            },
            _active: {
                borderWidth: '2px',
                borderColor: 'stokkeCore.black'
            }
        },
        label: {
            textAlign: 'left',
            paddingLeft: 2,
            // add top and bottom padding to match multi line design
            paddingTop: '2px',
            paddingBottom: '2px'
        },
        swatchButton: {}
    }
}

export default {
    parts: [
        'swatchGroup',
        'swatchLabel',
        'swatch',
        'swatchesWrapper',
        'swatchButton',
        'label',
        'displayLabel',
        'displayName'
    ],
    baseStyle: () => ({
        swatchGroup: {
            flexDirection: 'column',
            minWidth: 0
        },
        swatchLabel: {
            marginBottom: 3
        },
        swatch: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',

            position: 'relative',
            backgroundColor: 'stokkeCore.white',
            _focus: {
                outline: 'none',
                boxShadow: 'outline'
            }
        },
        swatchesWrapper: {
            flexWrap: {base: 'nowrap', lg: 'wrap'},
            overflow: 'auto',
            // prevent offscreen swatches containing visually hidden (position: absolute) elements
            // to cause horizontal scroll of the parent container
            position: 'relative'
        },
        swatchButton: {
            borderColor: 'stokkeGray.mid',
            gap: 2,
            _disabled: {
                opacity: 1
            }
        },
        displayLabel: {fontWeight: 'semibold'}
    }),
    variants: {
        circle: ({selected, disabled, propagateDisabled}) => ({
            swatch: {
                borderRadius: 'full',
                padding: 0,
                cursor: 'pointer',
                color: `${selected ? 'stokkeCore.black' : 'stokkeGray.mid'}`,
                borderColor: `${selected ? 'stokkeCore.black' : 'stokkeGray.mid'}`,
                borderStyle: 'solid',
                _hover: {
                    borderColor: `${selected ? 'stokkeCore.black' : 'stokkeGray.mid'}`,
                    borderStyle: 'solid'
                },
                _active: {
                    background: 'transparent'
                },
                _before: {
                    content: '""',
                    display: `${disabled ? 'block' : 'none'}`,
                    position: 'absolute',
                    zIndex: 1
                }
            },
            swatchButton: {
                borderColor: 'stokkeGray.mid',
                overflow: 'hidden',
                borderRadius: 'full',
                minWidth: 'auto',
                opacity: 1,
                _focus: {
                    outline: 'none'
                },
                pointerEvents: disabled && propagateDisabled ? 'none' : null
            }
        }),
        square: ({selected, disabled}) => ({
            swatch: {
                padding: 0,
                aspectRatio: '1/1',
                height: 'auto',
                borderColor: selected ? 'stokkeCore.black' : 'stokkeGray.mid',
                borderStyle: 'solid',
                borderRadius: '5px',
                borderWidth: selected ? '2px' : '1px',
                overflow: 'hidden',
                _focus: {outline: 'none'},
                _hover: {
                    textDecoration: 'none',
                    borderColor: 'stokkeCore.black'
                },
                _active: {
                    borderColor: 'stokkeCore.black'
                },
                _after: {
                    content: disabled ? '""' : 'none',
                    position: 'absolute',
                    // position so after rotation it is displayed in the top right corner
                    top: '-25%',
                    // we have 1px from the width and another half from the border
                    left: 'calc(50% - 1.5px)',
                    width: '1.5px',
                    // 150% height to make the line longer than the square's diagonal
                    height: '150%',
                    boxSizing: 'content',
                    transform: 'rotate(135deg)',
                    transformOrigin: 'center',
                    borderRight: '1px',
                    borderColor: 'stokkeGray.mid',
                    background: 'stokkeCore.black'
                }
            },
            swatchButton: {
                aspectRatio: '1/1',
                height: 'auto'
            }
        }),
        chip: ({selected, orderable, theme}) => chipStyles({selected, orderable, theme}),
        thumbnails: ({selected, orderable, theme}) => {
            return {
                ...chipStyles({selected, orderable, theme}),
                displayLabel: {textStyle: 'Semi Bold Small'},
                displayName: {textStyle: 'Regular Small'}
            }
        }
    },
    sizes: {
        sm: {
            swatch: {
                minWidth: 6,
                height: 6,
                width: 6,
                borderWidth: 1,
                _before: {
                    width: '1px',
                    height: 8,
                    transform: 'rotate(-45deg)',
                    backgroundColor: 'stokkeCore.black'
                }
            },
            swatchButton: {
                height: 4.5,
                width: 4.5,
                textStyle: 'Badge Regular'
            }
        },
        lg: ({selected}) => ({
            swatch: {
                minWidth: 12,
                height: 12,
                width: 12,
                borderWidth: `${selected ? '2px' : '1px'}`,
                _before: {
                    width: 9.5,
                    height: 9.5,
                    color: 'stokkeCore.black',
                    background: `linear-gradient(to top right,
                         transparent calc(50% - 0.5px),
                         currentColor calc(50% - 0.5px) calc(50% + 0.5px),
                         transparent calc(50% + 0.5px))
                         `
                }
            },
            swatchButton: {
                height: 10,
                width: 10
            }
        }),
        // this is used in combination with `aspectRatio` prop from the variant
        auto: {
            swatch: {
                width: '100%',
                height: 'auto'
            },
            swatchButton: {
                width: '100%',
                height: 'auto'
            }
        }
    },
    defaultProps: {
        variant: 'circle',
        size: 'lg'
    }
}
